import { useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import FileService from "../../services/doctorServices/FileService";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Headeer from "../../components/Headeer";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import ConfirmationDialog from "../../components/ConfirmationDialog";

export default function File() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('filesPage');
  const { id } = useParams();
  const fileService = new FileService();
  const [fileInfo, setFileInfo] = useState([]);
  const navigate = useNavigate();
  const [formsPaginationModel, setFormsPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [patientsPaginationModel, setPatientsPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [dialogState, setDialogState] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [formTypeDialogOpen, setFormTypeDialogOpen] = useState(false);

  const dataGridCardStyles = {
    mt: 5,
    width: "95%",
    mx: "auto",
    background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
    borderRadius: "16px",
    border: `1px solid ${colors.grey[800]}30`,
    boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
    overflow: "hidden",
  };

  const dataGridWrapperStyles = {
    px: 3,
    pb: 3,
    minHeight: 360,
    '& .MuiDataGrid-root': {
      border: 'none',
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: `${colors.blueAccent[700]}60`,
      borderBottom: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
      fontWeight: 700,
      fontSize: '0.95rem',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${colors.grey[800]}20`,
      fontSize: '0.9rem',
    },
    '& .name-column--cell': {
      color: colors.greenAccent[300],
    },
    '& .MuiDataGrid-row': {
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: `${colors.blueAccent[800]}20`,
      },
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: `${colors.primary[500]}20`,
    },
    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: `${colors.blueAccent[700]}40`,
      borderTop: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
    },
    '& .MuiTablePagination-root': {
      color: colors.grey[100],
    },
    '& .MuiCheckbox-root': {
      color: `${colors.greenAccent[200]} !important`,
    },
    '& .MuiDataGrid-toolbarContainer': {
      padding: 0,
    },
  };

  const CustomToolbar = ({ placeholder }) => (
    <GridToolbarContainer
      sx={{
        p: 2,
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        background: `linear-gradient(135deg, ${colors.blueAccent[800]}20 0%, ${colors.primary[500]} 100%)`,
        borderBottom: `2px solid ${colors.blueAccent[500]}30`,
        borderRadius: '12px 12px 0 0',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarDensitySelector
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarExport
          sx={{
            color: 'white',
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            border: `1px solid ${colors.greenAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}60`,
            },
            transition: 'all 0.3s ease',
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  useEffect(() => {
    // UUID format kontrolü: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (id && !uuidRegex.test(id)) {
      // Geçersiz UUID formatı, direkt 404'e yönlendir
      navigate("/404", { replace: true });
      return;
    }

    const fetchFileInfo = async () => {
      try {
        const response = await fileService.getFileInfo(id);
        // Response boş veya null ise 404'e yönlendir
        if (!response || !response.id) {
          navigate("/404", { replace: true });
          return;
        }
        setFileInfo(response);
        console.log("fileInfo", response);
        console.log("Forms with Creator:", response?.Forms?.map(f => ({
          id: f.id,
          name: f.name,
          created_by: f.created_by,
          Creator: f.Creator
        })));
      } catch (e) {
        console.log("Sorun oluştu.fetchFileInfo", e);
        // 404 veya geçersiz ID durumunda 404 sayfasına yönlendir
        if (e.response?.status === 404 || !e.response) {
          navigate("/404", { replace: true });
        } else {
          // Diğer hatalar için de 404'e yönlendir (file bulunamadı)
          navigate("/404", { replace: true });
        }
      }
    };
    fetchFileInfo();
  }, [id, navigate]);

  const createdAt = fileInfo?.createdAt?.split("T")[0] ?? "";
  const updatedAt = fileInfo?.updatedAt?.split("T")[0] ?? "";
  const totalForms = fileInfo?.Forms?.length ?? 0;
  const totalPatients = fileInfo?.patientCount ?? 0;

  const fileMetaDetails = [
    {
      label: t('fileName'),
      value: fileInfo?.name || "—",
      icon: <FolderOpenOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
    },
    {
      label: t('createdAt'),
      value: createdAt || "—",
      icon: <CalendarTodayOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[400]} 100%)`,
    },
    {
      label: t('totalForms'),
      value: totalForms,
      icon: <DescriptionOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
    },
    {
      label: t('totalPatients'),
      value: totalPatients,
      icon: <PeopleAltOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
    },
  ];

  const columns = [
    {
      field: "name",
      headerName: t('formName'),
      cellClassName: "name-column--cell",
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ color: colors.grey[100], fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: t('type'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "created_by",
      headerName: t('createdBy'),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        // Backend'den gelen Creator objesinden doktor adını al
        const creator = params.row.Creator;
        if (creator && creator.name && creator.surname) {
          return `${creator.name} ${creator.surname}`;
        }
        // Fallback: Eğer Creator yoksa created_by ID'sini göster
        const createdById = params.row.created_by;
        return createdById ? `${t('doctorPrefix')}${createdById}` : t('unknown');
      },
      renderCell: (params) => {
        const creator = params.row.Creator;
        const createdById = params.row.created_by;
        
        // Debug için console.log
        if (!creator && createdById) {
          console.log("Creator missing for form:", params.row.id, "created_by:", createdById);
        }
        
        const doctorName = creator && creator.name && creator.surname 
          ? `${creator.name} ${creator.surname}`
          : (createdById ? `${t('doctorPrefix')}${createdById}` : t('unknown'));
        
        return (
          <Chip
            label={doctorName}
            size="small"
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              color: colors.blueAccent[300],
              fontWeight: 600,
              border: `1px solid ${colors.blueAccent[500]}40`,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: t("actions"),
      width: 88,
      minWidth: 88,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={t("viewForm")}>
          <IconButton
            size="small"
            aria-label={t("viewForm")}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/files/${id}/${params.row.id}`);
            }}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              border: `1px solid ${colors.blueAccent[500]}40`,
              "&:hover": {
                backgroundColor: `${colors.blueAccent[600]}60`,
                transform: "scale(1.08)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const columnsPatients = [
    {
      field: "firstName",
      headerName: t('firstName'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "lastName",
      headerName: t('lastName'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "email",
      headerName: t('email'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => navigate(`/patients/${params.row.id}`)}
          sx={{ backgroundColor: colors.greenAccent[600] }}
          size="small"
        >
          {t('viewProfile')}
        </Button>
      ),
    },
  ];

  const handleDeleteFile = async () => {
    try {
      const response = await fileService.deleteFile(id);
      console.log("File Başarıyla Silindi", response.data);
      setSnackbar({
        open: true,
        message: t('fileDeletedSuccessfully'),
        severity: "success",
      });
      setDialogState(false);
      setTimeout(() => {
        navigate("/files");
      }, 1000);
    } catch (error) {
      console.log("File silinirken bir hata oluştu: ", error);
      setDialogState(false);
      
      // Check if it's a permission error (403)
      // Permission errors are already handled by global interceptor, so only show local error for other cases
      if (error.response?.status !== 403) {
        const errorMessage = error.response?.data?.message || t('errorDeletingFile');
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
      // For 403 errors, global snackbar from axios interceptor will be shown
    }
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", marginLeft: 2, marginRight: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Headeer title={t('fileDetail')} subtitle={t('displayFileDetail')} />
      </Box>
      <Box
        sx={{
          width: "95%",
          mx: "auto",
          mt: 3,
        }}
      >
          <Box
            sx={{
            position: "relative",
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 60%, ${colors.blueAccent[900]}80 100%)`,
            border: `1px solid ${colors.blueAccent[700]}40`,
            boxShadow: `0 16px 40px ${colors.primary[900]}50`,
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at top right, ${colors.blueAccent[700]}30, transparent 55%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: colors.grey[100], letterSpacing: 0.3 }}
              >
                {t('fileOverview')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], opacity: 0.85 }}
              >
                {t('fileOverviewDescription')}
              </Typography>
            </Box>
            <Chip
              label={`${t('updated')}: ${updatedAt || "—"}`}
              sx={{
                backgroundColor: `${colors.blueAccent[700]}40`,
                color: colors.blueAccent[100],
                fontWeight: 600,
                borderRadius: 2,
                px: 1.5,
              }}
            />
          </Box>
          <Grid container spacing={3} sx={{ position: "relative" }}>
            {fileMetaDetails.map((detail) => (
              <Grid item xs={12} md={6} key={detail.label}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
                    gap: 2,
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: `${colors.primary[600]}CC`,
                    border: `1px solid ${colors.primary[200]}30`,
                    boxShadow: `0 10px 28px ${colors.primary[900]}45`,
                    backdropFilter: "blur(6px)",
            }}
          >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 48,
                      height: 48,
                      background: detail.gradient,
                      color: colors.grey[100],
                      boxShadow: `0 6px 18px ${colors.primary[900]}40`,
                    }}
                  >
                    {detail.icon}
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: colors.grey[300],
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      {detail.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.grey[100],
                        fontSize: "1.15rem",
                        fontWeight: 600,
                        mt: 0.3,
                      }}
                    >
                      {detail.value}
                    </Typography>
                  </Box>
          </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      <Card sx={dataGridCardStyles}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            py: 3,
            borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                boxShadow: `0 6px 18px ${colors.blueAccent[500]}40`,
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: colors.grey[100] }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}
              >
                {t('forms')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}
              >
                {t('formsDescription')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            label={`${fileInfo?.Forms?.length ?? 0} ${(fileInfo?.Forms?.length ?? 0) === 1 ? t('form') : t('formsPlural')}`}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              color: colors.blueAccent[100],
              fontWeight: 600,
              borderRadius: 2,
              px: 1.5,
            }}
          />
            <Button
              variant="contained"
              onClick={() => setFormTypeDialogOpen(true)}
              sx={{
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                color: "white",
                fontWeight: 600,
                borderRadius: 2,
                px: 2.5,
                py: 1,
                textTransform: "none",
                boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
                  background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                },
                transition: "all 0.3s ease",
              }}
            >
              {t('addNewForm')}
            </Button>
          </Box>
        </Box>
        <Box sx={dataGridWrapperStyles}>
          <DataGrid
            rows={fileInfo?.Forms ?? []}
            columns={columns}
            autoHeight
            pagination
            paginationModel={formsPaginationModel}
            onPaginationModelChange={setFormsPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ toolbar: { placeholder: t('searchForms') } }}
            localeText={{
              noRowsLabel: (
                <Box sx={{ textAlign: "center", width: "100%", py: 6 }}>
                  <Typography variant="body1" sx={{ color: colors.grey[300] }}>
                    {t('noFormsMessage')}
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Card>

      <Card sx={dataGridCardStyles}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            py: 3,
            borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                boxShadow: `0 6px 18px ${colors.greenAccent[500]}35`,
          }}
        >
              <PeopleAltOutlinedIcon sx={{ color: colors.grey[100] }} />
            </Avatar>
            <Box>
          <Typography 
                variant="h4"
                sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}
          >
                {t('patientsOnFile')}
          </Typography>
          <Typography
                variant="body2"
                sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}
          >
                {t('patientsOnFileDescription')}
          </Typography>
            </Box>
          </Box>
          <Chip
            label={`${fileInfo?.patientCount ?? 0} ${(fileInfo?.patientCount ?? 0) === 1 ? t('patient') : t('patientsPlural')}`}
            sx={{
              backgroundColor: `${colors.greenAccent[700]}40`,
              color: colors.greenAccent[100],
              fontWeight: 600,
              borderRadius: 2,
              px: 1.5,
            }}
          />
        </Box>
        <Box sx={dataGridWrapperStyles}>
            <DataGrid
              rows={fileInfo?.patients ?? []}
              columns={columnsPatients}
              autoHeight
              pagination
            paginationModel={patientsPaginationModel}
            onPaginationModelChange={setPatientsPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ toolbar: { placeholder: t('searchPatients') } }}
              localeText={{
                noRowsLabel: (
                <Box sx={{ textAlign: "center", width: "100%", py: 6 }}>
                  <Typography variant="body1" sx={{ color: colors.grey[300] }}>
                      {t('noPatientsMessage')}
                    </Typography>
                </Box>
                ),
              }}
            />
        </Box>
      </Card>

      <Box
        sx={{
          display: "flex",
          mt: 5,
          justifyContent: "flex-end",
          width: "95%",
          mx: "auto",
        }}
      >
        <Button
          onClick={() => {
            setDialogState(true);
          }}
          startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} />}
          sx={{
            color: colors.grey[100],
            background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[500]} 100%)`,
            fontWeight: 700,
            borderRadius: 3,
            px: 3,
            py: 1.3,
            textTransform: "none",
            letterSpacing: 0.4,
            boxShadow: `0 12px 28px ${colors.redAccent[900]}45`,
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-3px) scale(1.02)",
              boxShadow: `0 18px 36px ${colors.redAccent[900]}60`,
              background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[500]} 100%)`,
            },
          }}
        >
          {t('deleteFile')}
        </Button>
      </Box>
      <ConfirmationDialog
        open={dialogState}
        title={t('deleteFileTitle')}
        description={t('deleteFileDescription')}
        onClose={() => setDialogState(false)}
        onConfirm={handleDeleteFile}
      />

      {/* Form Type Selection Dialog */}
      <Dialog
        open={formTypeDialogOpen}
        onClose={() => setFormTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
            borderRadius: '16px',
            border: `1px solid ${colors.blueAccent[500]}40`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: '1.5rem',
            borderBottom: `1px solid ${colors.blueAccent[500]}40`,
            pb: 2,
          }}
        >
          {t('selectFormType')}
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              mb: 3,
            }}
          >
            {t('chooseFormType')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                const typeParam = "for%20patients";
                navigate(`/Create-NewForm/${id}?type=${typeParam}`);
                setFormTypeDialogOpen(false);
              }}
              sx={{
                p: 3,
                border: `2px solid ${colors.greenAccent[500]}`,
                color: colors.grey[100],
                backgroundColor: `${colors.greenAccent[700]}20`,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  border: `2px solid ${colors.greenAccent[400]}`,
                  backgroundColor: `${colors.greenAccent[700]}40`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('forPatients')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {t('forPatientsDescription')}
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                const typeParam = "for%20me";
                navigate(`/Create-NewForm/${id}?type=${typeParam}`);
                setFormTypeDialogOpen(false);
              }}
              sx={{
                p: 3,
                border: `2px solid ${colors.blueAccent[500]}`,
                color: colors.grey[100],
                backgroundColor: `${colors.blueAccent[700]}20`,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  border: `2px solid ${colors.blueAccent[400]}`,
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('forMe')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {t('forMeDescription')}
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setFormTypeDialogOpen(false)}
            sx={{
              color: colors.grey[300],
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${colors.grey[700]}40`,
              },
            }}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: "100%",
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


